import React from 'react';
import { Group } from '@visx/group';
import { Pie as PieShape } from '@visx/shape';
import { PieArcDatum } from 'd3';
import { z } from 'zod/v4';
import {
  ChartAccessorFunctions,
  ChartDimensions,
  ChartFormatters,
  DATA_LABEL_MODE,
  DatumId,
  WithColorAccessorFunctions,
  WithDataLabels,
  WithMargin,
  WithPieVariant,
} from '../../core/Chart.types';
import { useChartInteractionDispatch, useChartInteractionState } from '../../core/ChartInteractionProvider';
import { formatPercentage } from '../../utils/formatters';
import { typedMemo } from '../../utils/typedMemo';
import { DonutCenter } from './DonutCenter';
import { computeSizeAndPosition } from './pie.utils';
import { PieSlice } from './PieSlice';
import { adjustArcs, computeArcPath, computeRingArc, DONUT_INNER_RADIUS_RATIO } from './PieSlice.utils';
import { computePolylineLabelCandidate, resolveVisiblePolylineLabels } from './polyline.utils';
import { SliceGroup } from './SliceGroup';
import { SliceLabel } from './SliceLabel';

// TODO: Unify tag + polyline collision into one Label component that renders any label given the available space per slice.

const defaultMargin = { top: 0, right: 0, bottom: 0, left: 0 };
const MemoizedPieSlice = typedMemo(PieSlice);

export type DonutCenterContentProps<Datum> = {
  centerValue?: string | number;
  centerLabel?: string;
  renderCenter?: (props: { innerRadius: number; data: Datum[]; highlightedItem: DatumId | null }) => React.ReactNode;
};

export type PieProps<Datum> = {
  data: Datum[];
  total?: number;
} & ChartAccessorFunctions<NoInfer<Datum>> &
  WithDataLabels &
  ChartDimensions &
  WithColorAccessorFunctions<NoInfer<Datum>> &
  WithPieVariant &
  DonutCenterContentProps<NoInfer<Datum>> &
  Partial<WithMargin>;

export function Pie<Datum>({
  getValue,
  getColor,
  getSelected,
  getIdentifier,
  valueFormatter,
  labelFormatter,
  width,
  height,
  data,
  dataLabelMode,
  variant = 'pie',
  centerValue,
  centerLabel,
  renderCenter,
  total,
  margin = defaultMargin,
}: PieProps<Datum> & ChartFormatters<Datum>) {
  const { setHighlight, handleItemClick, showTooltip, hideTooltip } = useChartInteractionDispatch<Datum>();
  const { highlightedItem, isInteractive, isPinned } = useChartInteractionState();
  const deferredHighlightedItem = React.useDeferredValue(highlightedItem);
  const { centerX, centerY, radius, effectiveDataLabelMode, polylineScale } = React.useMemo(
    () => computeSizeAndPosition(width, height, margin.top, margin.bottom, margin.left, margin.right, dataLabelMode),
    [width, height, margin.top, margin.bottom, margin.left, margin.right, dataLabelMode]
  );

  const isDonut = variant === 'donut';
  const innerRadius = isDonut ? radius * DONUT_INNER_RADIUS_RATIO : 0;

  const getDataLabel = (arc: PieArcDatum<Datum>) => valueFormatter?.(arc.data, { where: 'data-label' }) ?? getValue(arc.data);
  const getArcColor = (arc: PieArcDatum<Datum>) => getColor(arc.data);

  const extractItemDescriptor = React.useCallback(
    (e: React.MouseEvent<SVGGElement, MouseEvent>) => {
      const datumId = extractDatumIdFromEvent(e);
      const index = extractIndexFromEvent(e);
      const datum = data.find(d => getIdentifier(d) === datumId);
      if (!datum) throw new Error('Expected to find datum for id.');
      return { id: datumId, datum, index };
    },
    [data, getIdentifier]
  );

  const handleLeave = React.useCallback(() => {
    setHighlight(null);
    hideTooltip();
  }, [setHighlight, hideTooltip]);

  const handleClick = React.useCallback(
    (e: React.MouseEvent<SVGGElement, MouseEvent>) => handleItemClick(e, extractItemDescriptor(e)),
    [extractItemDescriptor, handleItemClick]
  );

  const handleEnter = React.useCallback(
    (e: React.PointerEvent<SVGPathElement>) => {
      const item = extractItemDescriptor(e);
      setHighlight(item);
      if (!isPinned) showTooltip({ data: item.datum, x: e.clientX, y: e.clientY });
    },
    [extractItemDescriptor, setHighlight, isPinned, showTooltip]
  );

  const handleHover = React.useCallback(
    (e: React.PointerEvent<SVGPathElement>) => (e.type === 'pointerleave' ? handleLeave() : handleEnter(e)),
    [handleEnter, handleLeave]
  );

  return (
    <Group top={centerY + margin.top} left={centerX + margin.left}>
      <PieShape data={data} pieValue={getValue} outerRadius={radius} innerRadius={innerRadius} cornerRadius={0}>
        {({ arcs, path }) => {
          const selectedIds = new Set<string>();
          const enriched = arcs.map(arc => {
            const id = getIdentifier(arc.data);
            const selected = !!getSelected?.(arc.data);
            if (selected) selectedIds.add(id);
            return {
              arc,
              id,
              selected,
              highlighted: id === deferredHighlightedItem,
              baseRadius: path.outerRadius()(arc) ?? 0,
            };
          });

          const adjustedAngles = adjustArcs(enriched);

          const slices = enriched.map((s, i) => {
            const adjusted = adjustedAngles[i];
            const { ringArc, ringInnerRadius, ringOuterRadius } = computeRingArc(adjusted, s.baseRadius, s.selected);
            return { ...s, adjusted, ringArc, ringInnerRadius, ringOuterRadius };
          });

          const visiblePolylineLabels =
            effectiveDataLabelMode === DATA_LABEL_MODE.polyline
              ? resolveVisiblePolylineLabels(
                  slices.map(s =>
                    computePolylineLabelCandidate(
                      s.id,
                      s.adjusted,
                      s.highlighted ? s.ringOuterRadius : s.ringInnerRadius,
                      getValue(s.arc.data),
                      polylineScale
                    )
                  ),
                  deferredHighlightedItem ?? undefined,
                  selectedIds.size > 0 ? selectedIds : undefined
                )
              : null;

          return (
            <g role="list" aria-label="Pie chart data" data-has-selection={selectedIds.size > 0 || undefined}>
              {slices.map(({ arc, adjusted, id, baseRadius, ringArc, ringInnerRadius, ringOuterRadius, highlighted, selected }) => {
                const arcPath = computeArcPath(adjusted, baseRadius, selected, innerRadius);
                if (!arcPath) throw new Error('Expected arcPath to be defined');

                const percentageRatio = total ? getValue(arc.data) / total : (arc.endAngle - arc.startAngle) / (2 * Math.PI);
                const sliceAriaLabel = `${labelFormatter?.(arc.data, { where: 'legend' })}, ${formatPercentage.format(percentageRatio)}`;
                const labelVisible = visiblePolylineLabels ? visiblePolylineLabels.has(id) : true;
                const color = getArcColor(arc);
                const label = getDataLabel(arc);
                return (
                  <SliceGroup
                    key={id}
                    role="listitem"
                    data-selected={selected || undefined}
                    data-highlighted={highlighted || undefined}
                    data-interactive={isInteractive ? '' : undefined}
                    data-index={arc.index}
                    data-datum-id={id}
                    onPointerEnter={handleHover}
                    onPointerLeave={handleHover}
                    onClick={handleClick}
                    aria-label={sliceAriaLabel}>
                    <MemoizedPieSlice
                      arcPath={arcPath}
                      ringArc={ringArc}
                      baseRadius={baseRadius}
                      innerRadius={innerRadius}
                      color={color}
                      selected={selected}
                      index={arc.index}
                      padAngle={arc.padAngle}
                      value={arc.value}
                      startAngle={adjusted.startAngle}
                      endAngle={adjusted.endAngle}
                    />

                    <SliceLabel
                      dataLabelMode={effectiveDataLabelMode}
                      label={label}
                      datum={arc.data}
                      angles={adjusted}
                      ringInnerRadius={ringInnerRadius}
                      ringOuterRadius={ringOuterRadius}
                      highlighted={highlighted}
                      selected={selected}
                      color={color}
                      total={total}
                      polylineScale={polylineScale}
                      visible={labelVisible}
                    />
                  </SliceGroup>
                );
              })}
            </g>
          );
        }}
      </PieShape>
      {isDonut &&
        (renderCenter ? (
          renderCenter({ innerRadius, data, highlightedItem })
        ) : (
          <DonutCenter innerRadius={innerRadius} data={data} getValue={getValue} centerValue={centerValue} centerLabel={centerLabel} />
        ))}
    </Group>
  );
}

const extractDatumIdFromEvent = (e: React.MouseEvent<SVGGElement, MouseEvent>) =>
  z.string().nonempty().parse(e.currentTarget.dataset.datumId);
const extractIndexFromEvent = (e: React.MouseEvent<SVGGElement, MouseEvent>) =>
  z.coerce.number().int().parse(e.currentTarget.dataset.index);
